/* eslint-disable max-len */
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { ClickOutsideModule } from 'ng-click-outside';
import { CodemirrorModule } from '@ctrl/ngx-codemirror';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { Overlay } from 'primeng/overlay';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { TreeModule } from 'primeng/tree';
import { RadioButtonModule } from 'primeng/radiobutton';
import { MessageModule } from 'primeng/message';
import { FieldsetModule } from 'primeng/fieldset';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { SliderModule } from 'primeng/slider';

import { LocalizationModule } from '../../Core/Localization/localization.module';
import { CheckboxFilterComponent } from './components/CheckboxFilter/checkbox-filter.component';
import { ChipContainerComponent } from './components/ChipContainer/chip-container.component';
import { ChipsComponent } from './components/Chips/chips.component';
import { ElementOverflowParentModule } from './directives/elementOverflowParent/element-overflow-parent.module';
import { CustomDropdownLabelComponent } from './components/CustomDropdownLabel/custom-dropdown-label.component';
import { CustomDropdownLabelDirective } from './directives/CustomDropdownLabel/custom-dropdown-label.directive';
import { DateFilterComponent } from './components/DateFilter/date-filter.component';
import { DeliveryRangeSchedulerComponent } from './components/DeliveryRangeScheduler/delivery-range-scheduler.component';
import { TimeHelper } from './helpers/time.helper';
import { DateHelper } from './helpers/date.helper';
import { HandleHelper } from './helpers/handle.helper';
import { FocusHelper } from './helpers/focus.helper';
import { DeliverySchedulerComponent } from './components/DeliveryScheduler/delivery-scheduler.component';
import { AddLeadingZeroModule } from './pipes/AddLeadingZero/add-leading-zero.module';
import { SetFocusModule } from './directives/setFocus/set-focus.module';
import { TimePickerComponent } from './components/TimePicker/time-picker.component';
import { TimezonePickerComponent } from './components/TimezonePicker/timezone-picker.component';
import { DateQueryInputComponent } from './components/DateQueryInput/date-query-input.component';
import { DateQueryPopupComponent } from './components/DateQueryPopup/date-query-popup.component';
import { IconDropdownComponent } from './components/IconDropdown/icon-dropdown.component';
import { ColorPickerComponent } from './components/ColorPicker/color-picker.component';
import { TagsApi } from '../../api/tags/tags-api';
import { TableComponent } from './components/Table/table.component';
import { ButtonComponent } from './components/Button/button.component';
import { MiscModule } from '../../Common/Misc/misc.module';
import { FormMessageComponent } from './components/FormMessage/form-message.component';
import { CheckboxComponent } from './components/Checkbox/checkbox.component';
import { LinkListComponent } from './components/LinkList/link-list.component';
import { MultiTimePickerComponent } from './components/MultiTimePicker/multi-time-picker.component';
import { DropdownChipsComponent } from './components/DropdownChips/dropdown.chips.component';
import { IncludeExcludeChipsComponent } from './components/IncludeExcludeChips/include.exclude.chips.component';
import { RadioComponent } from './components/Radio/radio.component';
import { DeliveryScheduleInputComponent } from './components/DeliveryScheduleInput/delivery-schedule-input.component';
import { TextareaInputComponent } from './components/TextareaInput/textarea-input.component';
import { NumberInputComponent } from './components/NumberInput/number-input.component';
import { PhoneNumberInputComponent } from './components/PhoneNumberInput/phone-number-input.component';
import { DropdownComponent } from './components/Dropdown/dropdown.component';
import { AutocompleteComponent } from './components/Autocomplete/autocomplete.component';
import { TreeListComponent } from './components/TreeList/tree-list.component';
import { DateRangeSelectorComponent } from './components/DateRangeSelector/date-range-selector.component';
import { MultiTextInputComponent } from './components/MultiTextInput/multi-text-input.component';
import { UsersModule } from '../../Core/Users/users.module';
import { RichTextEditorComponent } from './components/RichTextEditor/rich-text-editor.component';
import { EmailChipsComponent } from './components/EmailChips/email.chips.component';
import { MultiselectAutocompleteComponent } from './components/MultiselectAutocomplete/multiselect-autocomplete.component';
import { FileUploadComponent } from './components/FileUpload/file-upload.component';
import { ColorPickerTriggerComponent } from './components/ColorPickerTrigger/color-picker-trigger.component';
import { CommonBasicModule } from '../Basic/basic.module';
import { SliderComponent } from './components/Slider/slider.component';
import { SliderInputComponent } from './components/SliderInput/slider-input.component';
import { SplitButtonComponent } from './components/SplitButton/split-button.component';
import { TextInputComponent } from './components/TextInput/text-input.component';
import { InplaceComponent } from './components/Inplace/inplace.component';
import { JournodexQuickSearchComponent } from './components/JournodexQuickSearch/journodex-quick-search.component';
import { GroupedAutoCompleteComponent } from './components/GroupedAutocomplete/grouped-autocomplete.component';
import { PrimeAutocompleteOverrideOverlayPanelComponent } from './components/PrimeAutocompleteOverrideOverlayPanel/prime-autocomplete-override-overlay-panel.component';
import { EmailTextInputComponent } from './components/EmailTextInput/email-text-input.component';
import { AutocompleteChipsComponent } from './components/AutocompleteChips/autocomplete-chips.component';
import { TextInputChipsComponent } from './components/TextInputChips/text-input-chips.component';
import { MultiSelectDropdownComponent } from './components/MultiselectDropdown/multi-select-dropdown.component';
import { KebabMenuComponent } from './components/KebabMenu/kebab-menu.component';
import { ListFilterSelectComponent } from './components/ListFilterSelect/list-filter-select.component';
import { AIPromptSuggestionComponent } from './components/AiPromptSuggestions/AI-prompt-suggestions.component';
import { DatePickerComponent } from './components/DatePicker/date-picker.component';
import { SocialSwitchComponent } from './components/SocialSwitch/social-switch.component';
import { AISettingFormComponent } from './components/AISettingForm/AI-setting-form.component';
import { AiDisclaimerComponent } from './components/AiDisclaimer/ai-disclaimer.component';
import { AiGeneratedContentCardComponent } from './components/AiGeneratedContentCard/ai-generated-content-card.component';
import { PromptCardComponent } from './components/PromptCard/prompt-card.component';
import { MentionInsightCardComponent } from './components/MentionInsightCard/mention-insight-card.component';
import { TranslateService } from './services/translate.service';
import { GroupedTableComponent } from './components/GroupedTable/grouped-table.component';
import { RichTextEditorCkComponent } from './components/RichTextEditorCK/rich-text-editor-ck.component';
import { BubbleStatusComponent } from './components/BubbleStatus/bubble-status.component';
import { JournodexEntityListItemComponent } from './components/JournodexEntityListItem/journodex-entity-list-item.component';
import { PrimeAutocompleteComponent } from './components/PrimeAutocomplete/autocomplete';
import { MetaDataComponent } from './components/MetaData/meta-data.component';
import { SlideToggleComponent } from './components/SlideToggle/slide-toggle.component';
import { TabsToggleComponent } from './components/TabsToggle/tabs-toggle.component';
import { CopilotSwitchBannerComponent } from './components/CoPilotSwitchBanner/copilot-switch-banner.component';
import { AgenticUpsellBannerComponent } from './components/AgenticUpsellBanner/agentic-upsell-banner.component';
import { InsightCardComponent } from './components/InsightCard/insight-card.component';
import { ColorSwatchGridComponent } from './components/ColorSwatchGrid/color-swatch-grid.component';
import { ColorGradientPickerComponent } from './components/ColorGradientPicker/color-gradient-picker.component';
import { AdvancedColorPickerPopoverComponent } from './components/AdvancedColorPickerPopover/advanced-color-picker-popover.component';
import { OverlayModule } from '../Overlay/overlay.module';
import { DatePickerOverlayComponent } from './components/DatePickerOverlay/date-picker-overlay.component';
import { DatePickerPanelComponent } from './components/DatePickerPanel/date-picker-panel.component';

export const formsModuleImports = [
    LocalizationModule,
    ElementOverflowParentModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AutoCompleteModule,
    Overlay,
    MessageModule,
    SelectModule,
    DatePickerModule,
    TooltipModule,
    TreeModule,
    MultiSelectModule,
    FieldsetModule,
    SetFocusModule,
    AddLeadingZeroModule,
    PopoverModule,
    MiscModule,
    BrowserAnimationsModule,
    UsersModule,
    CodemirrorModule,
    CommonBasicModule,
    ToggleSwitchModule,
    ClickOutsideModule,
    ScrollingModule,
    CKEditorModule,
    SliderModule,
    OverlayModule
];

export const formsModuleDeclarations = [
    CheckboxFilterComponent,
    ChipContainerComponent,
    ChipsComponent,
    MultiselectAutocompleteComponent,
    EmailChipsComponent,
    CustomDropdownLabelComponent,
    CustomDropdownLabelDirective,
    DateQueryInputComponent,
    DateQueryPopupComponent,
    DateFilterComponent,
    DeliveryRangeSchedulerComponent,
    DeliverySchedulerComponent,
    DropdownChipsComponent,
    IncludeExcludeChipsComponent,
    IconDropdownComponent,
    ColorPickerComponent,
    TimePickerComponent,
    MultiTimePickerComponent,
    TimezonePickerComponent,
    TableComponent,
    TextInputComponent,
    ButtonComponent,
    FormMessageComponent,
    CheckboxComponent,
    LinkListComponent,
    TreeListComponent,
    RadioComponent,
    AutocompleteComponent,
    PrimeAutocompleteComponent,
    DeliveryScheduleInputComponent,
    TextareaInputComponent,
    NumberInputComponent,
    PhoneNumberInputComponent,
    DropdownComponent,
    DateRangeSelectorComponent,
    MultiTextInputComponent,
    RichTextEditorComponent,
    RichTextEditorCkComponent,
    FileUploadComponent,
    ColorPickerTriggerComponent,
    SliderComponent,
    SliderInputComponent,
    SplitButtonComponent,
    InplaceComponent,
    PrimeAutocompleteOverrideOverlayPanelComponent,
    JournodexQuickSearchComponent,
    GroupedAutoCompleteComponent,
    EmailTextInputComponent,
    AutocompleteChipsComponent,
    TextInputChipsComponent,
    MultiSelectDropdownComponent,
    KebabMenuComponent,
    ListFilterSelectComponent,
    AIPromptSuggestionComponent,
    DatePickerComponent,
    DatePickerOverlayComponent,
    DatePickerPanelComponent,
    SocialSwitchComponent,
    AISettingFormComponent,
    AiDisclaimerComponent,
    AiGeneratedContentCardComponent,
    PromptCardComponent,
    MentionInsightCardComponent,
    GroupedTableComponent,
    BubbleStatusComponent,
    JournodexEntityListItemComponent,
    MetaDataComponent,
    SlideToggleComponent,
    TabsToggleComponent,
    CopilotSwitchBannerComponent,
    AgenticUpsellBannerComponent,
    InsightCardComponent,
    ColorSwatchGridComponent,
    ColorGradientPickerComponent,
    AdvancedColorPickerPopoverComponent
];

@NgModule({
    imports: formsModuleImports,
    declarations: formsModuleDeclarations,
    exports: [
        CheckboxFilterComponent,
        ChipContainerComponent,
        ChipsComponent,
        MultiselectAutocompleteComponent,
        EmailChipsComponent,
        CustomDropdownLabelDirective,
        DateQueryInputComponent,
        DateQueryPopupComponent,
        DateFilterComponent,
        DeliveryRangeSchedulerComponent,
        DeliverySchedulerComponent,
        DropdownChipsComponent,
        IncludeExcludeChipsComponent,
        IconDropdownComponent,
        ColorPickerComponent,
        TimePickerComponent,
        MultiTimePickerComponent,
        TimezonePickerComponent,
        TableComponent,
        TextInputComponent,
        ButtonComponent,
        FormMessageComponent,
        CheckboxComponent,
        LinkListComponent,
        TreeListComponent,
        RadioComponent,
        AutocompleteComponent,
        DeliveryScheduleInputComponent,
        TextareaInputComponent,
        NumberInputComponent,
        PhoneNumberInputComponent,
        DropdownComponent,
        DateRangeSelectorComponent,
        MultiTextInputComponent,
        RichTextEditorComponent,
        RichTextEditorCkComponent,
        FileUploadComponent,
        ColorPickerTriggerComponent,
        SliderComponent,
        SliderInputComponent,
        SplitButtonComponent,
        InplaceComponent,
        PrimeAutocompleteOverrideOverlayPanelComponent,
        JournodexQuickSearchComponent,
        GroupedAutoCompleteComponent,
        EmailTextInputComponent,
        AutocompleteChipsComponent,
        TextInputChipsComponent,
        MultiSelectDropdownComponent,
        ToggleSwitchModule,
        KebabMenuComponent,
        ListFilterSelectComponent,
        AIPromptSuggestionComponent,
        DatePickerComponent,
        DatePickerOverlayComponent,
        DatePickerPanelComponent,
        SocialSwitchComponent,
        AISettingFormComponent,
        AiDisclaimerComponent,
        AiGeneratedContentCardComponent,
        PromptCardComponent,
        MentionInsightCardComponent,
        GroupedTableComponent,
        BubbleStatusComponent,
        JournodexEntityListItemComponent,
        MetaDataComponent,
        SlideToggleComponent,
        TabsToggleComponent,
        CopilotSwitchBannerComponent,
        AgenticUpsellBannerComponent,
        InsightCardComponent,
        ColorSwatchGridComponent,
        ColorGradientPickerComponent,
        AdvancedColorPickerPopoverComponent
    ],
    providers: [
        DateHelper,
        TimeHelper,
        HandleHelper,
        TagsApi,
        FocusHelper,
        TranslateService
    ]
})
export class CommonFormsModule { }
